import React, { useEffect, useState, useRef } from "react";
import { Stack, Card, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, LinearProgress, ButtonGroup, IconButton, TextField } from "@mui/material";
import { Lesson } from "../../types/Lessons";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

type QuestionaireProps = {
  lesson: Lesson;
  questions: Question[];
  onCompletion: (scores: Scores) => void;
}

type Question = {
  question: string;
  name: string;
  options: string[];
}

export type Scores = {[key: string]: string|number};

const Questionaire = (props: QuestionaireProps) => {

  const [step, setStep] = useState<number>(1);
  const [name, setName] = useState<string>();
  const [scores, setScores] = useState<Scores>(Object.fromEntries(props.questions.map(question => [question.name, props.lesson[question.name]])));

  const userInteractedRef = useRef(false);

  useEffect(() => {
    if (Object.values(scores).filter(score => score).length < props.questions.length) {  return }
    setStep(props.questions.length + 1);
  }, []);

  useEffect(() => {
    if (step > props.questions.length && userInteractedRef.current) { 
      setName(undefined);
      props.onCompletion(scores);
      userInteractedRef.current = false;
      return 
    } else if (step <= props.questions.length) {
      setName(props.questions[step-1].name);
    }
  }, [step]);

  const next = () => {
    if (step > props.questions.length) return;
    userInteractedRef.current = true;
    setStep(step + 1);
  };

  return <Stack component={Card} spacing={2}>
    { step <= props.questions.length ? <>
      <h3>{props.questions[step-1].question}</h3>

      <FormControl>
        { props.questions[step-1].options.length > 0 ? <>
          <RadioGroup name={name}>
            {props.questions[step-1].options.map((option, index) => {
              const value = props.questions[step-1].options.length - index;
              return <FormControlLabel key={`${name}_${value}`}
                                      value={value} 
                                      control={<Radio checked={scores[name!] === value} />}
                                      label={option} 
                                      onChange={() => {
                                        setScores(current => { return {...current, [name!]: value }} );
                                        next();
                                      }} />
            })}
          </RadioGroup>
        </> : <TextField name={name} 
                         variant="filled"
                         value={scores[name!] ?? ""}
                         onChange={e => setScores(current => { return {...current, [name!]: e.target.value }})} />
        }
      </FormControl>
    </> : <>
      <h3><TaskAltIcon color="info" sx={{verticalAlign: "-0.25rem"}} /> Questionaire completed</h3>                                                                                
    </> }

    <Stack direction="row" spacing={8} alignItems="center">
      <Stack direction="row" spacing={2} flexGrow={1} alignItems="center">
        <LinearProgress sx={{flexGrow: 1}} color="info" variant="determinate" value={(step-1)/props.questions.length*100} />
        <FormLabel>Step {step}/{props.questions.length+1}</FormLabel>
      </Stack>

      <ButtonGroup>
        <IconButton disabled={step <= 1} onClick={() => setStep(step-1)}><NavigateBeforeIcon /></IconButton>
        <IconButton disabled={(step < props.questions.length && !scores[name!]) || step > props.questions.length} onClick={next}><NavigateNextIcon /></IconButton>
      </ButtonGroup>
    </Stack>
  </Stack>
};

export default Questionaire;